/**
 * The SFX bank.
 *
 * Register: 8-bit arcade, one step short of parody. Square and pulse for
 * anything mechanical, filtered noise for anything violent, a sine sub under
 * anything that is supposed to move the desk. That sits beside the CRT
 * composite without either one apologising for the other.
 *
 * Everything here is data. `playSfx` is the only code path, which keeps the
 * hot loop honest: no closures, no arrays built per shot, no allocation until
 * something actually loops.
 */

import type { SynthEngine, VoiceSpec } from './Synth';

/** One voice inside a sound. `at` offsets it from the trigger instant. */
export interface SfxLayer extends VoiceSpec {
  /** Seconds after the trigger. Turns a patch table into a small sequencer. */
  at?: number;
}

export interface SfxDef {
  layers: readonly SfxLayer[];
  /** Higher survives voice stealing. UI 20, combat 40-60, structural 80+. */
  priority: number;
  /** Random detune per trigger, in cents. Kills machine-gun phasing. */
  detuneJitter?: number;
  /** Random envelope stretch per trigger, as a fraction. 0.12 = ±12%. */
  timeJitter?: number;
  /** Milliseconds of music ducking. */
  duck?: number;
  duckAmount?: number;
  /**
   * Seconds over which repeats of this same sound are counted. Past the third
   * hit inside the window the gain rolls off as 1/√n, so forty simultaneous
   * swarmling deaths read as one dense crunch instead of forty clipped blips.
   */
  crowdWindow?: number;
  /** Sustains until the returned handle is stopped. */
  loop?: boolean;
}

// ────────────────────────────────────────────────────────────────── bank ──

export const SFX = {
  // ── shoot, one timbre per tower family ──────────────────────────────────
  /** Rapid-fire pea shooter. Tiny, dry, gets out of the way. */
  'shoot.dart': {
    priority: 40, detuneJitter: 55, timeJitter: 0.16, crowdWindow: 0.09,
    layers: [
      { wave: 'square', freq: 880, freqEnd: 430, sweepShape: 'exp', dur: 0, attack: 0.001, decay: 0.045, release: 0.02, peak: 0.24, filter: 'lowpass', cutoff: 4200 },
    ],
  },
  /** Big single barrel. Sub thump under a filtered saw crack. */
  'shoot.cannon': {
    priority: 46, detuneJitter: 40, timeJitter: 0.1, crowdWindow: 0.12, duck: 90, duckAmount: 0.18,
    layers: [
      { wave: 'saw', freq: 240, freqEnd: 62, sweepShape: 'exp', noise: 0.34, noiseRate: 0.7, dur: 0.01, attack: 0.001, decay: 0.16, release: 0.07, peak: 0.5, filter: 'lowpass', cutoff: 2000, cutoffEnd: 320, q: 1.4 },
      { wave: 'sine', freq: 120, freqEnd: 44, sweepShape: 'exp', dur: 0, attack: 0.002, decay: 0.13, release: 0.05, peak: 0.4 },
    ],
  },
  /** Minigun. Narrow pulse, almost no tail, heavy per-shot jitter. */
  'shoot.gatling': {
    priority: 38, detuneJitter: 90, timeJitter: 0.22, crowdWindow: 0.07,
    layers: [
      { wave: 'pulse', width: 0.17, freq: 700, freqEnd: 480, sweepShape: 'exp', noise: 0.3, noiseRate: 1.6, dur: 0, attack: 0.0008, decay: 0.038, release: 0.018, peak: 0.22, filter: 'highpass', cutoff: 520, q: 1.1 },
    ],
  },
  /** Coherent beam pulse: rising, narrow-band, with a short echo tail. */
  'shoot.laser': {
    priority: 44, detuneJitter: 30, timeJitter: 0.09, crowdWindow: 0.1,
    layers: [
      { wave: 'saw', freq: 1750, freqEnd: 2900, sweepShape: 'lin', dur: 0.01, attack: 0.001, decay: 0.085, release: 0.05, peak: 0.3, filter: 'bandpass', cutoff: 2100, cutoffEnd: 3400, q: 7.5, echo: 0.22 },
    ],
  },
  /** Arc discharge. Mostly bandpassed noise, sweeping down hard. */
  'shoot.tesla': {
    priority: 46, detuneJitter: 70, timeJitter: 0.18, crowdWindow: 0.11,
    layers: [
      { wave: 'square', freq: 3000, freqEnd: 820, sweepShape: 'exp', noise: 0.8, noiseRate: 2.2, dur: 0, attack: 0.001, decay: 0.115, release: 0.06, peak: 0.34, filter: 'bandpass', cutoff: 2600, cutoffEnd: 900, q: 5.5, reverb: 0.16 },
    ],
  },
  /** Mortar launch: air displacement, not a bang. */
  'shoot.mortar': {
    priority: 48, detuneJitter: 35, timeJitter: 0.1, crowdWindow: 0.14, duck: 110, duckAmount: 0.2,
    layers: [
      { wave: 'sine', freq: 175, freqEnd: 46, sweepShape: 'exp', noise: 0.26, noiseRate: 0.55, dur: 0.02, attack: 0.004, decay: 0.22, release: 0.1, peak: 0.55, filter: 'lowpass', cutoff: 1000, cutoffEnd: 220, q: 1.8 },
    ],
  },
  /** Cryo jet. Bright, airy, long-ish, with a glassy tail. */
  'shoot.frost': {
    priority: 42, detuneJitter: 60, timeJitter: 0.15, crowdWindow: 0.12,
    layers: [
      { wave: 'tri', freq: 1450, freqEnd: 2500, sweepShape: 'exp', noise: 0.45, noiseRate: 1.9, dur: 0.01, attack: 0.006, decay: 0.17, release: 0.11, peak: 0.24, filter: 'highpass', cutoff: 1400, q: 2.2, reverb: 0.3 },
    ],
  },
  /** Corrosive gob. Wide pulse, wet lowpass, a little disgusting. */
  'shoot.acid': {
    priority: 42, detuneJitter: 80, timeJitter: 0.2, crowdWindow: 0.11,
    layers: [
      { wave: 'pulse', width: 0.78, freq: 540, freqEnd: 290, sweepShape: 'exp', noise: 0.38, noiseRate: 0.9, dur: 0, attack: 0.003, decay: 0.13, release: 0.07, peak: 0.28, filter: 'lowpass', cutoff: 1500, cutoffEnd: 640, q: 4.2 },
    ],
  },
  /** Railgun. The whole audible band collapses to a sub in 180 ms. */
  'shoot.rail': {
    priority: 52, detuneJitter: 25, timeJitter: 0.07, crowdWindow: 0.16, duck: 130, duckAmount: 0.24,
    layers: [
      { wave: 'saw', freq: 3400, freqEnd: 165, sweepShape: 'exp', noise: 0.22, noiseRate: 1.4, dur: 0.01, attack: 0.001, decay: 0.18, release: 0.12, peak: 0.5, filter: 'lowpass', cutoff: 6000, cutoffEnd: 380, q: 3.4, echo: 0.24 },
      { wave: 'sine', freq: 90, freqEnd: 40, sweepShape: 'exp', at: 0.012, dur: 0, attack: 0.002, decay: 0.2, release: 0.08, peak: 0.42 },
    ],
  },
  /** Gravity lance. Detuned sub pair with a slow wobble. */
  'shoot.singularity': {
    priority: 50, detuneJitter: 20, timeJitter: 0.08, crowdWindow: 0.18, duck: 120, duckAmount: 0.2,
    layers: [
      { wave: 'sine', freq: 62, wave2: 'saw', mix2: 0.45, ratio2: 1.5, detune2: 9, dur: 0.06, attack: 0.02, decay: 0.3, sustain: 0.25, release: 0.22, peak: 0.42, filter: 'lowpass', cutoff: 420, q: 6, lfoHz: 6.5, lfoCents: 55, reverb: 0.4 },
    ],
  },

  // ── impacts ─────────────────────────────────────────────────────────────
  /** Projectile connects. Deliberately tiny — it fires hundreds of times. */
  hit: {
    priority: 30, detuneJitter: 110, timeJitter: 0.25, crowdWindow: 0.06,
    layers: [
      { wave: 'square', freq: 1600, freqEnd: 900, sweepShape: 'exp', noise: 0.62, noiseRate: 1.7, dur: 0, attack: 0.0006, decay: 0.026, release: 0.015, peak: 0.16, filter: 'bandpass', cutoff: 1900, q: 2.4 },
    ],
  },

  // ── deaths, one per enemy family ────────────────────────────────────────
  /** Baseline pop with a short body. */
  'death.grunt': {
    priority: 52, detuneJitter: 90, timeJitter: 0.2, crowdWindow: 0.09,
    layers: [
      { wave: 'square', freq: 520, freqEnd: 120, sweepShape: 'exp', noise: 0.4, noiseRate: 1.1, dur: 0, attack: 0.001, decay: 0.12, release: 0.05, peak: 0.3, filter: 'lowpass', cutoff: 2600, cutoffEnd: 500 },
    ],
  },
  /** Fast, high, gone. */
  'death.runner': {
    priority: 50, detuneJitter: 120, timeJitter: 0.24, crowdWindow: 0.08,
    layers: [
      { wave: 'tri', freq: 1250, freqEnd: 330, sweepShape: 'exp', noise: 0.28, noiseRate: 2.3, dur: 0, attack: 0.0008, decay: 0.075, release: 0.035, peak: 0.26, filter: 'highpass', cutoff: 380 },
    ],
  },
  /** Plate failure: a clank, then the hull under it. */
  'death.armoured': {
    priority: 58, detuneJitter: 50, timeJitter: 0.14, crowdWindow: 0.13, duck: 80, duckAmount: 0.15,
    layers: [
      { wave: 'square', freq: 1900, freqEnd: 620, sweepShape: 'exp', noise: 0.7, noiseRate: 1.5, dur: 0, attack: 0.0008, decay: 0.06, release: 0.03, peak: 0.28, filter: 'bandpass', cutoff: 2400, q: 3.8 },
      { wave: 'saw', freq: 300, freqEnd: 74, sweepShape: 'exp', at: 0.03, noise: 0.3, noiseRate: 0.6, dur: 0.01, attack: 0.002, decay: 0.22, release: 0.1, peak: 0.36, filter: 'lowpass', cutoff: 1300, cutoffEnd: 260, q: 2.2 },
    ],
  },
  /** Swarmling. Almost nothing on its own; the crowd roll-off does the rest. */
  'death.swarmling': {
    priority: 34, detuneJitter: 190, timeJitter: 0.3, crowdWindow: 0.11,
    layers: [
      { wave: 'pulse', width: 0.3, freq: 1550, freqEnd: 680, sweepShape: 'exp', noise: 0.45, noiseRate: 2.6, dur: 0, attack: 0.0005, decay: 0.045, release: 0.02, peak: 0.13, filter: 'bandpass', cutoff: 2200, q: 2.8 },
    ],
  },
  /** Weaver. Wobbles on the way out, like it never travelled straight. */
  'death.weaver': {
    priority: 52, detuneJitter: 95, timeJitter: 0.2, crowdWindow: 0.1,
    layers: [
      { wave: 'sine', freq: 950, freqEnd: 210, sweepShape: 'exp', wave2: 'tri', mix2: 0.4, ratio2: 2.01, dur: 0, attack: 0.002, decay: 0.16, release: 0.08, peak: 0.28, filter: 'lowpass', cutoff: 3000, cutoffEnd: 700, lfoHz: 17, lfoCents: 130, reverb: 0.18 },
    ],
  },
  /** Splitter. Rises before it bursts, so the children feel earned. */
  'death.splitter': {
    priority: 56, detuneJitter: 70, timeJitter: 0.16, crowdWindow: 0.12,
    layers: [
      { wave: 'square', freq: 380, freqEnd: 980, sweepShape: 'exp', dur: 0, attack: 0.004, decay: 0.09, release: 0.02, peak: 0.24, filter: 'lowpass', cutoff: 3200 },
      { wave: 'saw', freq: 900, freqEnd: 190, sweepShape: 'exp', at: 0.1, noise: 0.5, noiseRate: 1.3, dur: 0, attack: 0.001, decay: 0.17, release: 0.08, peak: 0.34, filter: 'lowpass', cutoff: 2800, cutoffEnd: 420, q: 1.8 },
    ],
  },
  /** Shield collapse: glassy bandpass sweep with a long-ish plate tail. */
  'death.shielded': {
    priority: 56, detuneJitter: 60, timeJitter: 0.15, crowdWindow: 0.12,
    layers: [
      { wave: 'tri', freq: 4200, freqEnd: 760, sweepShape: 'exp', noise: 0.55, noiseRate: 2.4, dur: 0.01, attack: 0.001, decay: 0.24, release: 0.16, peak: 0.3, filter: 'bandpass', cutoff: 4000, cutoffEnd: 900, q: 6.5, reverb: 0.42 },
    ],
  },
  /** Boss. A sub that falls off a cliff, a roar, and 400 ms of ducked music. */
  'death.boss': {
    priority: 96, detuneJitter: 20, timeJitter: 0.05, duck: 620, duckAmount: 0.6,
    layers: [
      { wave: 'sine', freq: 96, freqEnd: 28, sweepShape: 'exp', sweepTime: 1.1, dur: 0.5, attack: 0.006, decay: 0.4, sustain: 0.5, release: 0.7, peak: 0.8, filter: 'lowpass', cutoff: 700, cutoffEnd: 90, q: 2.4 },
      { wave: 'saw', freq: 210, freqEnd: 55, sweepShape: 'exp', sweepTime: 0.9, noise: 0.6, noiseRate: 0.5, dur: 0.35, attack: 0.02, decay: 0.5, sustain: 0.35, release: 0.6, peak: 0.5, filter: 'lowpass', cutoff: 1800, cutoffEnd: 200, q: 3, reverb: 0.5 },
      { wave: 'square', freq: 1400, freqEnd: 300, sweepShape: 'exp', at: 0.02, noise: 0.85, noiseRate: 1.8, dur: 0, attack: 0.001, decay: 0.3, release: 0.2, peak: 0.35, filter: 'highpass', cutoff: 900, reverb: 0.35 },
    ],
  },

  // ── ordnance ────────────────────────────────────────────────────────────
  /** Splash damage. Crack, body, rumble — the three parts of any good bang. */
  explosion: {
    priority: 82, detuneJitter: 45, timeJitter: 0.14, crowdWindow: 0.14, duck: 300, duckAmount: 0.45,
    layers: [
      { wave: 'square', freq: 2200, freqEnd: 500, sweepShape: 'exp', noise: 0.9, noiseRate: 2.0, dur: 0, attack: 0.0006, decay: 0.07, release: 0.04, peak: 0.42, filter: 'highpass', cutoff: 1100 },
      { wave: 'saw', freq: 420, freqEnd: 70, sweepShape: 'exp', noise: 0.75, noiseRate: 0.75, dur: 0.02, attack: 0.002, decay: 0.34, release: 0.22, peak: 0.6, filter: 'lowpass', cutoff: 2400, cutoffEnd: 190, q: 1.6, reverb: 0.3 },
      { wave: 'sine', freq: 130, freqEnd: 34, sweepShape: 'exp', at: 0.008, dur: 0.03, attack: 0.003, decay: 0.3, release: 0.24, peak: 0.62 },
    ],
  },
  /** Chain lightning. Three zaps, each higher and thinner than the last. */
  chainZap: {
    priority: 62, detuneJitter: 100, timeJitter: 0.18, crowdWindow: 0.13,
    layers: [
      { wave: 'square', freq: 2400, freqEnd: 900, sweepShape: 'exp', noise: 0.8, noiseRate: 2.1, dur: 0, attack: 0.0008, decay: 0.07, release: 0.04, peak: 0.3, filter: 'bandpass', cutoff: 2400, q: 6, echo: 0.2 },
      { wave: 'square', freq: 3100, freqEnd: 1200, sweepShape: 'exp', at: 0.045, noise: 0.8, noiseRate: 2.4, dur: 0, attack: 0.0008, decay: 0.06, release: 0.035, peak: 0.24, filter: 'bandpass', cutoff: 3100, q: 6.5, echo: 0.24 },
      { wave: 'square', freq: 3900, freqEnd: 1500, sweepShape: 'exp', at: 0.092, noise: 0.85, noiseRate: 2.8, dur: 0, attack: 0.0008, decay: 0.055, release: 0.03, peak: 0.19, filter: 'bandpass', cutoff: 3900, q: 7, echo: 0.28, reverb: 0.2 },
    ],
  },
  /** Held beam. Runs until the handle is stopped. */
  laserLoop: {
    priority: 60, loop: true, detuneJitter: 15,
    layers: [
      { wave: 'saw', freq: 260, wave2: 'saw', mix2: 0.5, detune2: 14, dur: 0, attack: 0.03, decay: 0.06, sustain: 0.85, release: 0.09, peak: 0.24, filter: 'bandpass', cutoff: 1500, q: 5.5, lfoHz: 24, lfoCents: 22, lfoCutoff: 260, echo: 0.18 },
    ],
  },
  /** Slow applied. Descending glass, long tail. */
  freeze: {
    priority: 55, detuneJitter: 45, timeJitter: 0.12, crowdWindow: 0.2,
    layers: [
      { wave: 'tri', freq: 2600, freqEnd: 640, sweepShape: 'exp', noise: 0.5, noiseRate: 2.2, dur: 0.01, attack: 0.004, decay: 0.3, release: 0.22, peak: 0.26, filter: 'highpass', cutoff: 900, q: 1.6, reverb: 0.45 },
      { wave: 'sine', freq: 1046, at: 0.05, wave2: 'sine', mix2: 0.5, ratio2: 1.5, dur: 0.04, attack: 0.01, decay: 0.24, sustain: 0.2, release: 0.3, peak: 0.16, reverb: 0.5 },
    ],
  },
  /** Corrosive pool. A held hiss, not a one-shot. */
  acidHiss: {
    priority: 44, loop: true, detuneJitter: 30,
    layers: [
      { wave: 'pulse', width: 0.85, freq: 180, noise: 0.9, noiseRate: 1.1, dur: 0, attack: 0.06, decay: 0.1, sustain: 0.8, release: 0.25, peak: 0.16, filter: 'bandpass', cutoff: 1700, q: 3.2, lfoHz: 3.3, lfoCutoff: 700 },
    ],
  },
  /** Black hole idle. Sub drone with a very slow filter breath. */
  singularityHum: {
    priority: 58, loop: true, detuneJitter: 10,
    layers: [
      { wave: 'sine', freq: 43, wave2: 'saw', mix2: 0.4, ratio2: 1.5, detune2: -11, dur: 0, attack: 0.5, decay: 0.4, sustain: 0.9, release: 0.9, peak: 0.3, filter: 'lowpass', cutoff: 260, q: 7, lfoHz: 0.27, lfoCents: 18, lfoCutoff: 130, reverb: 0.5 },
    ],
  },

  // ── build interactions ──────────────────────────────────────────────────
  /** Tower placed. Two-note confirmation with a mechanical seat under it. */
  place: {
    priority: 70, detuneJitter: 20, timeJitter: 0.05,
    layers: [
      { wave: 'pulse', width: 0.4, freq: 392, dur: 0, attack: 0.002, decay: 0.07, release: 0.03, peak: 0.26, filter: 'lowpass', cutoff: 3200 },
      { wave: 'pulse', width: 0.4, freq: 587, at: 0.07, dur: 0.01, attack: 0.002, decay: 0.1, release: 0.06, peak: 0.24, filter: 'lowpass', cutoff: 4000, reverb: 0.16 },
      { wave: 'sine', freq: 160, freqEnd: 70, sweepShape: 'exp', noise: 0.35, noiseRate: 0.8, dur: 0, attack: 0.001, decay: 0.09, release: 0.04, peak: 0.3, filter: 'lowpass', cutoff: 900 },
    ],
  },
  /** Tower sold. The place cue, inverted, plus a coin. */
  sell: {
    priority: 70, detuneJitter: 20, timeJitter: 0.05,
    layers: [
      { wave: 'pulse', width: 0.4, freq: 587, dur: 0, attack: 0.002, decay: 0.07, release: 0.03, peak: 0.24, filter: 'lowpass', cutoff: 3600 },
      { wave: 'pulse', width: 0.4, freq: 392, at: 0.07, dur: 0, attack: 0.002, decay: 0.09, release: 0.05, peak: 0.22, filter: 'lowpass', cutoff: 3000 },
      { wave: 'tri', freq: 2093, at: 0.02, wave2: 'tri', mix2: 0.5, ratio2: 1.5, dur: 0, attack: 0.001, decay: 0.16, release: 0.1, peak: 0.14, filter: 'highpass', cutoff: 1200, reverb: 0.3 },
    ],
  },

  // ── run structure ───────────────────────────────────────────────────────
  /** Round begins. Fifth stab plus a drum. */
  waveStart: {
    priority: 88, detuneJitter: 10, duck: 220, duckAmount: 0.3,
    layers: [
      { wave: 'saw', freq: 220, wave2: 'saw', mix2: 0.5, ratio2: 1.4983, dur: 0.06, attack: 0.006, decay: 0.18, sustain: 0.4, release: 0.3, peak: 0.4, filter: 'lowpass', cutoff: 2600, cutoffEnd: 900, q: 2, reverb: 0.3 },
      { wave: 'sine', freq: 150, freqEnd: 50, sweepShape: 'exp', dur: 0, attack: 0.002, decay: 0.18, release: 0.08, peak: 0.5 },
      { wave: 'square', freq: 880, at: 0.14, wave2: 'square', mix2: 0.5, ratio2: 1.5, dur: 0.02, attack: 0.004, decay: 0.16, sustain: 0.3, release: 0.24, peak: 0.2, filter: 'lowpass', cutoff: 4000, echo: 0.25 },
    ],
  },
  /** Boss incoming. Two-tone siren over a tritone. Unmissable by design. */
  bossWarn: {
    priority: 98, detuneJitter: 8, duck: 1400, duckAmount: 0.5,
    layers: [
      { wave: 'saw', freq: 233, wave2: 'saw', mix2: 0.5, ratio2: 1.4142, detune2: 6, dur: 0.22, attack: 0.03, decay: 0.1, sustain: 0.75, release: 0.2, peak: 0.34, filter: 'lowpass', cutoff: 1400, q: 3.5, reverb: 0.35 },
      { wave: 'saw', freq: 311, at: 0.42, wave2: 'saw', mix2: 0.5, ratio2: 1.4142, detune2: -6, dur: 0.22, attack: 0.03, decay: 0.1, sustain: 0.75, release: 0.2, peak: 0.34, filter: 'lowpass', cutoff: 1600, q: 3.5, reverb: 0.35 },
      { wave: 'saw', freq: 233, at: 0.84, wave2: 'saw', mix2: 0.5, ratio2: 1.4142, detune2: 6, dur: 0.3, attack: 0.03, decay: 0.12, sustain: 0.7, release: 0.4, peak: 0.36, filter: 'lowpass', cutoff: 1400, q: 3.5, reverb: 0.45 },
      { wave: 'sine', freq: 58, dur: 1.0, attack: 0.2, decay: 0.3, sustain: 0.8, release: 0.5, peak: 0.5 },
    ],
  },
  /** Enemy reached the exit. Dull, wrong, sits low in the mix. */
  leak: {
    priority: 90, detuneJitter: 15, duck: 260, duckAmount: 0.35,
    layers: [
      { wave: 'tri', freq: 330, dur: 0.04, attack: 0.005, decay: 0.14, sustain: 0.4, release: 0.2, peak: 0.3, filter: 'lowpass', cutoff: 1200, reverb: 0.3 },
      { wave: 'tri', freq: 277, at: 0.11, dur: 0.1, attack: 0.005, decay: 0.2, sustain: 0.4, release: 0.4, peak: 0.32, filter: 'lowpass', cutoff: 900, reverb: 0.4 },
      { wave: 'sine', freq: 82, freqEnd: 55, sweepShape: 'exp', sweepTime: 0.5, dur: 0.2, attack: 0.01, decay: 0.3, sustain: 0.5, release: 0.4, peak: 0.4 },
    ],
  },
  /** Run over. Four-note descent, heavy plate, no drums. */
  defeat: {
    priority: 100, duck: 4000, duckAmount: 0.9,
    layers: [
      { wave: 'saw', freq: 220, wave2: 'saw', mix2: 0.5, detune2: 12, dur: 0.24, attack: 0.02, decay: 0.2, sustain: 0.6, release: 0.4, peak: 0.34, filter: 'lowpass', cutoff: 1600, cutoffEnd: 800, q: 2, reverb: 0.5 },
      { wave: 'saw', freq: 185, at: 0.34, wave2: 'saw', mix2: 0.5, detune2: 12, dur: 0.24, attack: 0.02, decay: 0.2, sustain: 0.6, release: 0.4, peak: 0.34, filter: 'lowpass', cutoff: 1400, cutoffEnd: 700, q: 2, reverb: 0.5 },
      { wave: 'saw', freq: 165, at: 0.68, wave2: 'saw', mix2: 0.5, detune2: 12, dur: 0.28, attack: 0.02, decay: 0.24, sustain: 0.6, release: 0.5, peak: 0.34, filter: 'lowpass', cutoff: 1200, cutoffEnd: 600, q: 2, reverb: 0.55 },
      { wave: 'saw', freq: 110, at: 1.02, wave2: 'saw', mix2: 0.55, ratio2: 0.5, detune2: 9, dur: 0.9, attack: 0.04, decay: 0.5, sustain: 0.55, release: 1.4, peak: 0.42, filter: 'lowpass', cutoff: 1000, cutoffEnd: 260, q: 2.4, reverb: 0.7 },
    ],
  },
  /** Run won. Major arpeggio with a shimmer on top. */
  victory: {
    priority: 100, duck: 3200, duckAmount: 0.85,
    layers: [
      { wave: 'pulse', width: 0.35, freq: 261.63, dur: 0.06, attack: 0.005, decay: 0.14, sustain: 0.5, release: 0.2, peak: 0.28, filter: 'lowpass', cutoff: 4000, reverb: 0.3 },
      { wave: 'pulse', width: 0.35, freq: 329.63, at: 0.13, dur: 0.06, attack: 0.005, decay: 0.14, sustain: 0.5, release: 0.2, peak: 0.28, filter: 'lowpass', cutoff: 4400, reverb: 0.3 },
      { wave: 'pulse', width: 0.35, freq: 392.0, at: 0.26, dur: 0.06, attack: 0.005, decay: 0.14, sustain: 0.5, release: 0.24, peak: 0.28, filter: 'lowpass', cutoff: 4800, reverb: 0.35 },
      { wave: 'pulse', width: 0.35, freq: 523.25, at: 0.39, dur: 0.1, attack: 0.005, decay: 0.18, sustain: 0.55, release: 0.35, peak: 0.3, filter: 'lowpass', cutoff: 5200, reverb: 0.4, echo: 0.2 },
      { wave: 'tri', freq: 1046.5, at: 0.52, wave2: 'tri', mix2: 0.5, ratio2: 1.5, dur: 0.7, attack: 0.06, decay: 0.4, sustain: 0.5, release: 1.1, peak: 0.22, filter: 'highpass', cutoff: 700, reverb: 0.65, echo: 0.3 },
      { wave: 'sine', freq: 65.41, dur: 1.0, attack: 0.03, decay: 0.5, sustain: 0.6, release: 0.8, peak: 0.4 },
    ],
  },

  // ── UI ──────────────────────────────────────────────────────────────────
  /** Pointer crosses a control. Barely there on purpose. */
  uiHover: {
    priority: 18, detuneJitter: 25, timeJitter: 0.1, crowdWindow: 0.05,
    layers: [
      { wave: 'sine', freq: 1320, dur: 0, attack: 0.001, decay: 0.024, release: 0.012, peak: 0.07, filter: 'highpass', cutoff: 700 },
    ],
  },
  /** Control accepted. */
  uiClick: {
    priority: 24, detuneJitter: 15, timeJitter: 0.06,
    layers: [
      { wave: 'square', freq: 720, freqEnd: 960, sweepShape: 'exp', dur: 0, attack: 0.001, decay: 0.032, release: 0.018, peak: 0.14, filter: 'lowpass', cutoff: 4200 },
    ],
  },
  /** Control refused: cannot afford it, cannot build there. */
  uiDeny: {
    priority: 26,
    layers: [
      { wave: 'pulse', width: 0.22, freq: 165, dur: 0, attack: 0.001, decay: 0.055, release: 0.02, peak: 0.18, filter: 'lowpass', cutoff: 1500, q: 1.4 },
      { wave: 'pulse', width: 0.22, freq: 138, at: 0.075, dur: 0, attack: 0.001, decay: 0.075, release: 0.03, peak: 0.18, filter: 'lowpass', cutoff: 1200, q: 1.4 },
    ],
  },

  // ── upgrade, sequenced by tier in `playUpgrade` ─────────────────────────
  /** Placeholder patch. The real cue is built per tier; see `playUpgrade`. */
  upgrade: {
    priority: 76, detuneJitter: 12, duck: 180, duckAmount: 0.25,
    layers: [
      { wave: 'pulse', width: 0.35, freq: 523.25, dur: 0.02, attack: 0.003, decay: 0.12, sustain: 0.4, release: 0.18, peak: 0.26, filter: 'lowpass', cutoff: 4200, reverb: 0.25 },
    ],
  },
} as const satisfies Record<string, SfxDef>;

export type SfxName = keyof typeof SFX;

/** Tower families, in the order the shop lists them. */
export const TOWER_FAMILIES = [
  'dart', 'cannon', 'gatling', 'laser', 'tesla', 'mortar', 'frost', 'acid', 'rail', 'singularity',
] as const;
export type TowerFamily = (typeof TOWER_FAMILIES)[number];

/** Maps a tower family (or any unknown id) onto a shoot cue. */
export function shootSfx(family: string): SfxName {
  const key = `shoot.${family}` as SfxName;
  return key in SFX ? key : 'shoot.dart';
}

/** Maps an `EnemyFamily` (or any unknown id) onto a death cue. */
export function deathSfx(family: string): SfxName {
  const key = `death.${family}` as SfxName;
  return key in SFX ? key : 'death.grunt';
}

// ──────────────────────────────────────────────────────────────── playing ──

export interface SfxOptions {
  /** Buffer-space x, 0..VIEW.width. Converted to pan by the facade. */
  x?: number;
  /** Explicit pan, -1..1. Wins over `x`. */
  pan?: number;
  /** Linear gain multiplier. */
  gain?: number;
  /** Extra detune in cents, on top of the patch jitter. */
  detune?: number;
  /** Playback rate for the envelope. >1 is slower. */
  rate?: number;
  /** Seconds from now. */
  delay?: number;
  /** Upgrade tier, 1..5. Only read by `upgrade`. */
  tier?: number;
  /** Forces a looping trigger even if the patch is a one-shot. */
  loop?: boolean;
}

/** Live control over a looping cue. One-shots never allocate one of these. */
export interface SfxHandle {
  readonly active: boolean;
  stop(release?: number): void;
  setFreq(hz: number, glide?: number): void;
  setCutoff(hz: number, glide?: number): void;
  setGain(v: number, glide?: number): void;
  setPan(v: number, glide?: number): void;
}

/** Crowd bookkeeping, preallocated so the hot path never touches the heap. */
interface Crowd { windowEnd: number; count: number }
const crowd: Record<string, Crowd> = Object.create(null) as Record<string, Crowd>;
for (const key in SFX) crowd[key] = { windowEnd: 0, count: 0 };

const jitterRand = () => Math.random() * 2 - 1;

class LoopHandle implements SfxHandle {
  private readonly engine: SynthEngine;
  private readonly voiceIndex: number;
  private readonly generation: number;

  constructor(engine: SynthEngine, voiceIndex: number, generation: number) {
    this.engine = engine;
    this.voiceIndex = voiceIndex;
    this.generation = generation;
  }

  private get voice() {
    const v = this.engine.sfx.voices[this.voiceIndex];
    return v.generation === this.generation ? v : null;
  }

  get active(): boolean { return this.voice !== null; }
  stop(release = 0.12): void { this.voice?.release(this.engine.now, release); }
  setFreq(hz: number, glide = 0.06): void { this.voice?.setFreq(hz, this.engine.now, glide); }
  setCutoff(hz: number, glide = 0.06): void { this.voice?.setCutoff(hz, this.engine.now, glide); }
  setGain(v: number, glide = 0.06): void { this.voice?.setGain(v, this.engine.now, glide); }
  setPan(v: number, glide = 0.04): void { this.voice?.setPan(v, this.engine.now, glide); }
}

/**
 * Triggers one entry from the bank.
 *
 * Returns a handle only for looping cues — one-shots return `null` so the
 * thirty-shots-per-second path stays allocation-free.
 */
export function playSfx(engine: SynthEngine, name: SfxName, opts?: SfxOptions): SfxHandle | null {
  const def: SfxDef = SFX[name];
  const now = engine.now;
  const at = now + (opts?.delay ?? 0);

  // Crowd control. Past three hits inside the window the level rolls off as
  // 1/√n and beyond twelve the request is dropped outright.
  let crowdGain = 1;
  const window = def.crowdWindow ?? 0;
  if (window > 0) {
    const c = crowd[name];
    if (now > c.windowEnd) { c.windowEnd = now + window; c.count = 0; }
    c.count++;
    if (c.count > 12) return null;
    if (c.count > 3) crowdGain = Math.sqrt(3 / c.count);
  }

  if (name === 'upgrade') return playUpgrade(engine, def, at, opts, crowdGain);

  const pan = opts?.pan ?? 0;
  const gain = (opts?.gain ?? 1) * crowdGain;
  const detuneBase = opts?.detune ?? 0;
  const rate = opts?.rate ?? 1;
  const loop = opts?.loop ?? def.loop ?? false;
  const dj = def.detuneJitter ?? 0;
  const tj = def.timeJitter ?? 0;

  if (def.duck !== undefined) engine.duck(def.duck, def.duckAmount);

  let handle: SfxHandle | null = null;
  const layers = def.layers;
  for (let i = 0; i < layers.length; i++) {
    const layer = layers[i];
    const v = engine.sfx.alloc(def.priority, now);
    if (v === null) continue;
    const scale = rate * (1 + jitterRand() * tj);
    trigOpts.pan = pan;
    trigOpts.gain = gain;
    trigOpts.detune = detuneBase + jitterRand() * dj;
    trigOpts.timeScale = scale;
    trigOpts.loop = loop && i === 0;
    trigOpts.priority = def.priority;
    v.trigger(layer, at + (layer.at ?? 0) * rate, trigOpts);
    if (trigOpts.loop) handle = new LoopHandle(engine, engine.sfx.voices.indexOf(v), v.generation);
  }
  return handle;
}

/** Reused trigger options. Read synchronously inside `Voice.trigger`. */
const trigOpts = { pan: 0, gain: 1, detune: 0, timeScale: 1, loop: false, priority: 0 };

/** Scale degrees of a major pentatonic run, in semitones. */
const PENTA = [0, 2, 4, 7, 9, 12, 14, 16] as const;

/**
 * The upgrade cue, built per tier. Higher tiers start lower, climb further and
 * end on a longer, wetter note — so tier 5 lands like an arrival and tier 1
 * lands like a receipt.
 */
function playUpgrade(engine: SynthEngine, def: SfxDef, at: number, opts: SfxOptions | undefined, crowdGain: number): null {
  const tier = Math.max(1, Math.min(5, Math.round(opts?.tier ?? 1)));
  const notes = 2 + tier;
  const root = 261.63 * Math.pow(2, (tier - 1) / 12);
  const pan = opts?.pan ?? 0;
  const gain = (opts?.gain ?? 1) * crowdGain;
  const step = 0.062 - tier * 0.004;

  if (def.duck !== undefined) engine.duck(def.duck + tier * 60, def.duckAmount);

  for (let i = 0; i < notes; i++) {
    const v = engine.sfx.alloc(def.priority, engine.now);
    if (v === null) continue;
    const last = i === notes - 1;
    upgradeSpec.freq = root * Math.pow(2, PENTA[i] / 12);
    upgradeSpec.width = 0.5 - tier * 0.05;
    upgradeSpec.dur = last ? 0.06 + tier * 0.03 : 0.01;
    upgradeSpec.decay = last ? 0.22 : 0.09;
    upgradeSpec.release = last ? 0.3 + tier * 0.06 : 0.06;
    upgradeSpec.peak = 0.2 + tier * 0.018;
    upgradeSpec.reverb = last ? 0.25 + tier * 0.06 : 0.12;
    upgradeSpec.echo = last && tier >= 3 ? 0.24 : 0;
    upgradeSpec.mix2 = tier >= 4 ? 0.4 : 0;
    upgradeSpec.cutoff = 3200 + tier * 700;
    trigOpts.pan = pan;
    trigOpts.gain = gain;
    trigOpts.detune = jitterRand() * (def.detuneJitter ?? 0);
    trigOpts.timeScale = 1;
    trigOpts.loop = false;
    trigOpts.priority = def.priority;
    v.trigger(upgradeSpec, at + i * step, trigOpts);
  }

  // Tier 5 gets a sub underneath, because tier 5 should feel structural.
  if (tier >= 5) {
    const v = engine.sfx.alloc(def.priority, engine.now);
    if (v !== null) {
      trigOpts.gain = gain;
      trigOpts.detune = 0;
      v.trigger(upgradeSub, at, trigOpts);
    }
  }
  return null;
}

/** Mutated in place by `playUpgrade`. Never escapes this module. */
const upgradeSpec: VoiceSpec = {
  wave: 'pulse', width: 0.4, freq: 523.25, dur: 0.02,
  attack: 0.003, decay: 0.12, sustain: 0.45, release: 0.18, peak: 0.26,
  filter: 'lowpass', cutoff: 4200, q: 1.2, wave2: 'pulse', mix2: 0, ratio2: 2, detune2: 7,
  reverb: 0.25, echo: 0,
};

const upgradeSub: VoiceSpec = {
  wave: 'sine', freq: 65.41, freqEnd: 130.81, sweepShape: 'exp', sweepTime: 0.4,
  dur: 0.3, attack: 0.02, decay: 0.3, sustain: 0.6, release: 0.5, peak: 0.4,
  filter: 'lowpass', cutoff: 400,
};
